import { useCallback, useState, type FC } from "react";
import { Modal, TimePicker, message } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { CalendarDays, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DashboardAntDatePicker } from "src/components/reusable/AntdDatePicker/DashboardAntDatePicker";
import CampaignFormField from "../../../../shared/CampaignFormField";
import type { ScheduleSelection } from "../hooks/UseCampaignDelivery";

const SCHEDULE_KEY = "campaignFlow.aiWizard.schedule";
const DATE_FIELD_ID = "schedule-campaign-date";
const TIME_FIELD_ID = "schedule-campaign-time";
const MODAL_WIDTH = 380;

const MINUTE_STEP = 5;

const isPastDate = (current: Dayjs) => current.isBefore(dayjs().startOf("day"));

const range = (count: number) => Array.from({ length: count }, (_, index) => index);

interface ScheduleCampaignModalProps {
  isSubmitting: boolean;
  onCancel: () => void;
  onSchedule: (_selection: ScheduleSelection) => void;
}

const ScheduleCampaignModal: FC<ScheduleCampaignModalProps> = ({
  isSubmitting,
  onCancel,
  onSchedule,
}) => {
  const { t } = useTranslation();
  const [date, setDate] = useState<Dayjs | null>(null);
  const [time, setTime] = useState<Dayjs | null>(null);
  const isToday = Boolean(date?.isSame(dayjs(), "day"));

  const disabledHours = useCallback(() => (isToday ? range(dayjs().hour()) : []), [isToday]);
  const disabledMinutes = useCallback(
    (hour: number) => (isToday && hour === dayjs().hour() ? range(dayjs().minute() + 1) : []),
    [isToday]
  );

  const handleConfirm = useCallback(() => {
    if (!date || !time) {
      message.error(t(`${SCHEDULE_KEY}.selectDateTime`));
      return;
    }

    const scheduledAt = date.hour(time.hour()).minute(time.minute()).second(0).millisecond(0);
    if (!scheduledAt.isAfter(dayjs())) {
      message.error(t(`${SCHEDULE_KEY}.futureTime`));
      return;
    }

    onSchedule({
      sendAt: scheduledAt.toISOString(),
      date: scheduledAt.format("YYYY-MM-DD"),
      time: scheduledAt.format("HH:mm"),
    });
  }, [date, onSchedule, t, time]);

  return (
    <Modal
      open
      centered
      closable={false}
      footer={null}
      width={MODAL_WIDTH}
      onCancel={onCancel}
      className="schedule-campaign-modal campaign-flow__popup"
    >
      <div className="schedule-campaign-modal__heading">
        <h3 className="schedule-campaign-modal__title">{t(`${SCHEDULE_KEY}.title`)}</h3>
        <p className="schedule-campaign-modal__subtitle">{t(`${SCHEDULE_KEY}.subtitle`)}</p>
      </div>

      <CampaignFormField label={t(`${SCHEDULE_KEY}.selectDate`)} htmlFor={DATE_FIELD_ID}>
        <DashboardAntDatePicker
          id={DATE_FIELD_ID}
          value={date}
          onChange={setDate}
          format="DD/MM/YYYY"
          placeholder={t(`${SCHEDULE_KEY}.datePlaceholder`)}
          disabledDate={isPastDate}
          suffixIcon={<CalendarDays size={14} aria-hidden />}
          popupClassName="campaign-flow__popup"
          className="schedule-campaign-modal__picker"
        />
      </CampaignFormField>

      <CampaignFormField label={t(`${SCHEDULE_KEY}.selectTime`)} htmlFor={TIME_FIELD_ID}>
        <TimePicker
          id={TIME_FIELD_ID}
          value={time}
          onChange={setTime}
          format="hh:mm A"
          use12Hours
          minuteStep={MINUTE_STEP}
          showNow={false}
          disabledHours={disabledHours}
          disabledMinutes={disabledMinutes}
          needConfirm={false}
          placeholder={t(`${SCHEDULE_KEY}.timePlaceholder`)}
          suffixIcon={<Clock size={14} aria-hidden />}
          popupClassName="campaign-flow__popup schedule-time-popup"
          className="schedule-campaign-modal__picker"
        />
      </CampaignFormField>

      <div className="schedule-campaign-modal__actions">
        <button
          type="button"
          className="button button--outline campaign-flow__action"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {t(`${SCHEDULE_KEY}.cancel`)}
        </button>
        <button
          type="button"
          className="button button--primary campaign-flow__action"
          onClick={handleConfirm}
          disabled={!date || !time || isSubmitting}
        >
          {isSubmitting ? t(`${SCHEDULE_KEY}.scheduling`) : t(`${SCHEDULE_KEY}.confirm`)}
        </button>
      </div>
    </Modal>
  );
};

export default ScheduleCampaignModal;
